/**
 * "My links" (#2089 SIDECFG): the one sidebar module a user shapes themselves.
 * Pages, gadgets and external links, in the order they keep, stored per user
 * in D1 (lib/sidebar-entries.ts). It renders the way the catalog modules do
 * (Sidebar.tsx NavSection / CollapsedSection) so the rail reads as one list.
 *
 * An entry is a pointer, not a grant. A page the role cannot open renders
 * with a lock and the same line the route guard shows when that page is
 * refused (refusedPageNotice); the click still goes through the guard, which
 * lands the user on their first page with that line, exactly as a deep link
 * would. Nothing here widens or narrows what a role can reach.
 */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router";
import {
  Bookmark,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useAllowedPages } from "@/hooks/use-rbac";
import { useSidebarEntries } from "@/lib/sidebar-entries";
import { isPathAllowed, isUniversalPage } from "@shared/rbac";
import { refusedPageLabel, refusedPageNotice } from "@shared/page-reachability";
import { SIDEBAR_SECTIONS, sectionKeyForPath } from "@shared/sidebar-pages";
import {
  SIDEBAR_ENTRY_LABEL_MAX,
  sidebarEntryGatePath,
  sidebarEntryHref,
  sidebarLinkTargetError,
  type SidebarEntry,
} from "@shared/sidebar-entries";
import { navResetState } from "@shared/nav-reset";
import { moduleRowClass, railIconClass, readCollapsedSections, subItemClass, writeCollapsedSection } from "./sidebar-shared";

export const MY_LINKS_LABEL = "My links";

/** The label a page entry is saved under: its catalog name, else the path itself.
 *  A section's own "Dashboard" page is named by its section ("Production Dashboard"),
 *  because three sections each call their first page Dashboard. */
export function currentPageLabel(pathname: string): string {
  const label = refusedPageLabel(pathname);
  if (!label) return pathname;
  if (label !== "Dashboard") return label;
  const key = sectionKeyForPath(pathname);
  const section = SIDEBAR_SECTIONS.find((s) => s.key === key);
  return section ? `${section.label} ${label}` : label;
}

/** The label a pasted link is saved under: its host without a www. prefix. */
export function linkDefaultLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "").slice(0, SIDEBAR_ENTRY_LABEL_MAX);
  } catch {
    return url.slice(0, SIDEBAR_ENTRY_LABEL_MAX);
  }
}

/** Null when the role can open the entry; else the refusal line that page shows. */
export function entryLockNotice(entry: Pick<SidebarEntry, "kind" | "target">, allowedPages: string[] | null): string | null {
  const gate = sidebarEntryGatePath(entry);
  if (!gate || allowedPages === null) return null;
  if (gate === "/" || isUniversalPage(gate) || isPathAllowed(gate, allowedPages)) return null;
  return refusedPageNotice(gate);
}

export function MyLinksSection({
  railCollapsed,
  onClose,
}: {
  railCollapsed: boolean;
  onClose: () => void;
}) {
  const links = useSidebarEntries();
  const allowedPages = useAllowedPages();
  if (links.unavailable) return null;
  return railCollapsed ? (
    <CollapsedMyLinks links={links} allowedPages={allowedPages} onClose={onClose} />
  ) : (
    <ExpandedMyLinks links={links} allowedPages={allowedPages} onClose={onClose} />
  );
}

type Links = ReturnType<typeof useSidebarEntries>;

/** The current location as a page target: path plus query, so a tab deep link is kept. */
function useCurrentTarget() {
  const { pathname, search } = useLocation();
  return { target: `${pathname}${search}`, pathname };
}

function ExpandedMyLinks({ links, allowedPages, onClose }: { links: Links; allowedPages: string[] | null; onClose: () => void }) {
  const [expanded, setExpanded] = useState(() => readCollapsedSections()[MY_LINKS_LABEL] !== true);
  const [adding, setAdding] = useState(false);
  const toggle = () =>
    setExpanded((prev) => {
      writeCollapsedSection(MY_LINKS_LABEL, prev);
      return !prev;
    });
  const empty = links.entries.length === 0;
  const showAdd = expanded && (adding || empty);

  return (
    <div className="mb-0.5" data-testid="my-links">
      <div className="flex items-center">
        <button type="button" onClick={toggle} aria-expanded={expanded} className={moduleRowClass(false)}>
          <Bookmark className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate text-left">{MY_LINKS_LABEL}</span>
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`} />
        </button>
        <button
          type="button"
          aria-label={adding ? "Close add" : "Add a page or link"}
          title={adding ? "Close" : "Add"}
          onClick={() => {
            setExpanded(true);
            setAdding((v) => !v);
          }}
          className="mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground lg:h-7 lg:w-7"
        >
          {adding ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        </button>
      </div>
      {expanded && (
        <div className="mb-1 ml-5 mt-0.5 space-y-px border-l border-sidebar-border">
          {links.entries.map((entry, i) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              lock={entryLockNotice(entry, allowedPages)}
              first={i === 0}
              last={i === links.entries.length - 1}
              links={links}
              onClose={onClose}
            />
          ))}
          {showAdd && <AddPanel links={links} onDone={() => setAdding(false)} />}
        </div>
      )}
    </div>
  );
}

function EntryLink({
  entry,
  lock,
  onClose,
  className,
}: {
  entry: SidebarEntry;
  lock: string | null;
  onClose: () => void;
  className: string;
}) {
  const href = sidebarEntryHref(entry);
  const body = (
    <>
      {lock && <Lock className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
      {entry.kind === "link" && <ExternalLink className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />}
    </>
  );
  if (entry.kind === "link") {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClose} className={className} title={entry.target}>
        {body}
      </a>
    );
  }
  return (
    <NavLink
      to={href}
      state={navResetState()}
      onClick={onClose}
      title={lock ?? undefined}
      aria-describedby={undefined}
      className={className}
      data-locked={lock ? "true" : undefined}
    >
      {body}
    </NavLink>
  );
}

function EntryRow({
  entry,
  lock,
  first,
  last,
  links,
  onClose,
}: {
  entry: SidebarEntry;
  lock: string | null;
  first: boolean;
  last: boolean;
  links: Links;
  onClose: () => void;
}) {
  const { pathname } = useLocation();
  // The menu is portaled to <body> with fixed placement: the nav column is
  // overflow-y-auto and the aside carries a transform, so an in-tree panel on
  // the last rows is clipped by the rail's bottom edge (first local capture).
  const [menu, setMenu] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(entry.label);
  const rowRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menu || typeof document === "undefined") return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rowRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenu(null);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const openMenu = () => {
    if (menu) {
      setMenu(null);
      return;
    }
    const rect = moreRef.current?.getBoundingClientRect();
    if (!rect || typeof window === "undefined") {
      setMenu({ top: 0, left: 0 });
      return;
    }
    const MENU_H = 190; // four rows; 44 px each below lg (#4334)
    const left = Math.max(8, rect.right - 144);
    if (rect.bottom + MENU_H + 8 > window.innerHeight) setMenu({ bottom: window.innerHeight - rect.top + 4, left });
    else setMenu({ top: rect.bottom + 4, left });
  };

  const active = entry.kind !== "link" && isPathAllowed(pathname, [sidebarEntryHref(entry).split(/[?#]/)[0]]);
  const rowClass = `${subItemClass(active)} min-w-0 flex-1 ${lock ? "text-sidebar-foreground/55" : ""}`;

  const commitRename = () => {
    const next = draft.trim();
    setRenaming(false);
    if (next && next !== entry.label) void links.rename(entry.id, next);
    else setDraft(entry.label);
  };
  const onRenameKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commitRename();
    if (e.key === "Escape") {
      setDraft(entry.label);
      setRenaming(false);
    }
  };

  const act = (fn: () => void) => () => {
    setMenu(null);
    fn();
  };

  return (
    <div ref={rowRef} className="group relative flex items-center" data-testid="my-links-entry" data-kind={entry.kind}>
      {renaming ? (
        <input
          autoFocus
          value={draft}
          maxLength={SIDEBAR_ENTRY_LABEL_MAX}
          aria-label="Rename"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={onRenameKey}
          className="ml-2 h-11 min-w-0 flex-1 rounded-lg border border-sidebar-border bg-background px-2 text-sm lg:h-8"
        />
      ) : (
        <EntryLink entry={entry} lock={lock} onClose={onClose} className={rowClass} />
      )}
      {lock && <span className="sr-only">{lock}</span>}
      <button
        ref={moreRef}
        type="button"
        aria-label={`Options for ${entry.label}`}
        aria-haspopup="menu"
        aria-expanded={!!menu}
        onClick={openMenu}
        className={`mr-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-opacity hover:bg-sidebar-accent/60 hover:text-sidebar-foreground focus:opacity-100 lg:h-7 lg:w-7 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 ${menu ? "lg:opacity-100" : ""}`}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {menu && (
        <RowMenu anchor={menu} menuRef={menuRef}>
          <MenuItem icon={ChevronUp} label="Move up" disabled={first} onClick={act(() => void links.move(entry.id, "up"))} />
          <MenuItem icon={ChevronDown} label="Move down" disabled={last} onClick={act(() => void links.move(entry.id, "down"))} />
          <MenuItem icon={Pencil} label="Rename" onClick={act(() => { setDraft(entry.label); setRenaming(true); })} />
          <MenuItem icon={Trash2} label="Remove" destructive onClick={act(() => void links.remove(entry.id))} />
        </RowMenu>
      )}
    </div>
  );
}

/** The row menu, fixed at the ⋯ button and portaled past the rail's clipping. Without a
 *  document (the test renderer) it renders in place so the items are still reachable. */
function RowMenu({
  anchor,
  menuRef,
  children,
}: {
  anchor: { top?: number; bottom?: number; left: number };
  menuRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  const node = (
    <div
      ref={menuRef}
      role="menu"
      style={{ top: anchor.top, bottom: anchor.bottom, left: anchor.left }}
      className="fixed z-[60] min-w-36 rounded-lg border border-border bg-popover p-1 text-sm shadow-lg"
    >
      {children}
    </div>
  );
  return typeof document === "undefined" ? node : createPortal(node, document.body);
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: typeof ChevronUp;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors disabled:opacity-40 lg:min-h-0 ${
        destructive ? "text-destructive hover:bg-destructive/10" : "text-foreground/85 hover:bg-accent hover:text-accent-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

/** Add this page, or paste a link. Shown when the section is empty or the + is open.
 *  #4334: every control here, like the + and ⋯ above, is 44 px below lg (the phone
 *  drawer) and keeps its desktop size from lg up. */
function AddPanel({ links, onDone }: { links: Links; onDone: () => void }) {
  const { target, pathname } = useCurrentTarget();
  const pageLabel = currentPageLabel(pathname);
  const saved = links.find("page", target);
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The panel is the last thing on the rail; opened below the fold it would be invisible.
    panelRef.current?.scrollIntoView?.({ block: "nearest" });
  }, []);

  const addPage = async () => {
    setError(null);
    setBusy(true);
    try {
      await links.add({ kind: "page", target, label: pageLabel });
      onDone();
    } catch (e) {
      setError((e as Error).message || "Could not add this page.");
    } finally {
      setBusy(false);
    }
  };
  const addLink = async (e: FormEvent) => {
    e.preventDefault();
    const clean = url.trim();
    const bad = sidebarLinkTargetError(clean);
    if (bad) {
      setError(bad);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await links.add({ kind: "link", target: clean, label: linkDefaultLabel(clean) });
      setUrl("");
      onDone();
    } catch (err) {
      setError((err as Error).message || "Could not add that link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={panelRef} className="ml-2 space-y-1 pb-1 pt-0.5" data-testid="my-links-add">
      <button
        type="button"
        onClick={addPage}
        disabled={busy || !!saved}
        className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 py-1 text-left text-sm font-medium text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground disabled:opacity-50 lg:min-h-8"
      >
        <Plus className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{saved ? "This page is saved" : `Add ${pageLabel}`}</span>
      </button>
      <form onSubmit={addLink} className="flex items-center gap-1 pl-3 pr-1">
        <input
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste a link"
          aria-label="Paste a link"
          className="h-11 min-w-0 flex-1 rounded-lg border border-sidebar-border bg-background px-2 text-sm placeholder:text-muted-foreground/70 lg:h-8"
        />
        <button
          type="submit"
          disabled={busy || !url.trim()}
          className="h-11 min-w-11 shrink-0 rounded-lg bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 lg:h-8 lg:min-w-0"
        >
          Add
        </button>
      </form>
      {error && (
        <p role="alert" className="px-3 text-xs text-rose-600 dark:text-rose-400">
          {error}
        </p>
      )}
    </div>
  );
}

/** Collapsed rail: one bookmark icon; hover or focus flies the list out, same as a catalog module. */
function CollapsedMyLinks({ links, allowedPages, onClose }: { links: Links; allowedPages: string[] | null; onClose: () => void }) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fly, setFly] = useState<{ top: number; left: number } | null>(null);
  const { pathname } = useLocation();
  const active = links.entries.some((e) => e.kind !== "link" && isPathAllowed(pathname, [sidebarEntryHref(e).split(/[?#]/)[0]]));

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxPanel = Math.min(window.innerHeight * 0.7, 480);
    setFly({ top: Math.max(8, Math.min(rect.top, window.innerHeight - maxPanel - 8)), left: rect.right + 6 });
  };
  const hide = () => {
    hideTimer.current = setTimeout(() => setFly(null), 150);
  };
  const closeAll = () => {
    setFly(null);
    onClose();
  };

  return (
    <div onMouseEnter={show} onMouseLeave={hide} data-testid="my-links">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => (fly ? setFly(null) : show())}
        onFocus={show}
        onBlur={hide}
        title={MY_LINKS_LABEL}
        className={railIconClass(active)}
      >
        <Bookmark className="h-4 w-4" />
      </button>
      {fly &&
        createPortal(
          <div
            style={{ top: fly.top, left: fly.left, maxHeight: "70vh" }}
            className="fixed z-[60] min-w-44 max-w-64 overflow-y-auto rounded-lg border border-border bg-popover p-1.5 shadow-lg"
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{MY_LINKS_LABEL}</p>
            {links.entries.map((entry) => {
              const lock = entryLockNotice(entry, allowedPages);
              return (
                <EntryLink
                  key={entry.id}
                  entry={entry}
                  lock={lock}
                  onClose={closeAll}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                    lock ? "text-foreground/50" : "text-foreground/75"
                  } hover:bg-accent/60 hover:text-foreground`}
                />
              );
            })}
            <CollapsedAddPage links={links} onDone={closeAll} />
          </div>,
          document.body,
        )}
    </div>
  );
}

function CollapsedAddPage({ links, onDone }: { links: Links; onDone: () => void }) {
  const { target, pathname } = useCurrentTarget();
  const saved = links.find("page", target);
  if (saved) return null;
  return (
    <button
      type="button"
      onClick={() => void links.add({ kind: "page", target, label: currentPageLabel(pathname) }).then(onDone, () => {})}
      className="mt-0.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
    >
      <Plus className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">Add {currentPageLabel(pathname)}</span>
    </button>
  );
}
